import { useEffect, useRef } from 'react';
import { LearningStreakApi } from '@/services/learning-streak.service';
import { useAuth } from '@/contexts/shared/AuthContext';

const TICK_MS = 60_000;          // ping cadence
const IDLE_LIMIT_MS = 120_000;   // only credit time if the user acted in the last 2 min
const MAX_SECONDS = 90;          // server also caps; keep the client modest

/**
 * Accumulates *active* learning time and pings the backend ~once a minute while
 * the tab is visible and the user is interacting. The server just does a Redis
 * INCRBY, so this stays cheap at scale. Mount once in the authenticated layout.
 */
export const useLearningHeartbeat = (): void => {
  const { user } = useAuth();
  const lastTick = useRef<number>(Date.now());
  const lastActivity = useRef<number>(Date.now());

  useEffect(() => {
    if (!user?.id) return;

    const markActive = () => { lastActivity.current = Date.now(); };
    const events: (keyof WindowEventMap)[] = [
      'mousemove', 'keydown', 'scroll', 'click', 'touchstart', 'pointerdown',
    ];
    events.forEach((e) => window.addEventListener(e, markActive, { passive: true }));

    const flush = () => {
      const now = Date.now();
      if (document.hidden) { lastTick.current = now; return; }
      const elapsedSec = (now - lastTick.current) / 1000;
      const idleMs = now - lastActivity.current;
      lastTick.current = now;
      if (idleMs > IDLE_LIMIT_MS || elapsedSec <= 0) return;
      const seconds = Math.min(Math.round(elapsedSec), MAX_SECONDS);
      if (seconds > 0) LearningStreakApi.heartbeat(seconds).catch(() => { /* ignore */ });
    };

    const interval = window.setInterval(flush, TICK_MS);
    const onVisibility = () => {
      if (document.hidden) flush();          // capture time before hiding
      else lastTick.current = Date.now();    // don't count time spent hidden
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.clearInterval(interval);
      events.forEach((e) => window.removeEventListener(e, markActive));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id]);
};
